import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  Mail,
  Brain,
  Shield,
  CheckCircle,
  ArrowRight,
  Clock,
  Zap,
  AlertCircle,
  MessageSquare,
  Database,
  TrendingUp,
  Users,
  FileText,
  Globe,
  Lock,
  Activity,
  Workflow,
  Handshake
} from 'lucide-react';

const AIEmailPrevention = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const aiCapabilities = [
    {
      icon: <Brain className="w-6 h-6 text-primary" />,
      title: "Advanced Natural Language Processing",
      description: "State-of-the-art NLP models trained specifically on AP communications understand context, intent, and urgency in supplier emails.",
      technical: "Transformer-based architecture with domain-specific fine-tuning on 10M+ AP email samples"
    },
    {
      icon: <Database className="w-6 h-6 text-primary" />,
      title: "Real-Time ERP Data Access",
      description: "Instant access to PO status, invoice history, payment schedules, and vendor records enables contextually aware responses.",
      technical: "Sub-100ms API response times with cached frequently accessed data patterns"
    },
    {
      icon: <MessageSquare className="w-6 h-6 text-primary" />,
      title: "Conversational Intelligence", 
      description: "Goes beyond keywords to understand supplier relationships, payment terms, and escalation patterns for nuanced communication.",
      technical: "Multi-turn dialogue management with persistent conversation memory and relationship mapping"
    },
    {
      icon: <Shield className="w-6 h-6 text-primary" />,
      title: "Compliance & Audit Trail",
      description: "Every AI decision is logged with reasoning, confidence scores, and audit trails for regulatory compliance and continuous improvement.",
      technical: "Immutable blockchain-backed audit logs with 99.99% uptime SLA"
    }
  ];

  const emailScenarios = [
    {
      type: "Payment Status Inquiry",
      complexity: "Simple",
      example: "\"When will invoice #12345 be paid?\"",
      response: "AI checks ERP payment schedule, identifies net terms, and provides specific payment date with any relevant details about approval status.",
      prevention: "Eliminates 80% of payment status calls and reduces AP team interruptions by 6 hours/day on average."
    },
    {
      type: "Invoice Discrepancy Resolution",
      complexity: "Complex", 
      example: "\"The PO amount doesn't match our invoice - there's a $500 difference on line item 3\"",
      response: "AI cross-references PO line items, identifies the discrepancy source, suggests resolution path, and can automatically initiate 3-way matching review.",
      prevention: "Resolves 65% of discrepancies without human intervention, reducing resolution time from 3 days to 30 minutes."
    },
    {
      type: "Urgent Payment Request",
      complexity: "Critical",
      example: "\"We need payment today or we're stopping shipments - this is affecting our cash flow\"",
      response: "AI assesses relationship risk, checks payment terms, identifies emergency payment options, and escalates to appropriate manager with full context.",
      prevention: "Prevents supplier relationship damage and supply chain disruptions by resolving payment-term and timing issues before they escalate."
    },
    {
      type: "New Vendor Onboarding",
      complexity: "Process-Heavy",
      example: "\"How do we get set up as a vendor? We need to submit invoices electronically.\"",
      response: "AI guides through vendor setup process, provides forms, explains requirements, and tracks completion status automatically.",
      prevention: "Reduces vendor setup time from 2 weeks to 2 days, eliminating back-and-forth and incomplete submissions."
    }
  ];

  const technicalArchitecture = [
    {
      component: "Email Ingestion Layer",
      description: "Multi-protocol email processing with spam filtering and security scanning",
      specs: ["IMAP/POP3/Exchange integration", "99.9% spam detection accuracy", "Sub-second processing"]
    },
    {
      component: "NLP Processing Engine", 
      description: "Advanced language models with AP domain specialization",
      specs: ["Transformer architecture", "Context window: 32K tokens", "Domain-tuned intent recognition"]
    },
    {
      component: "ERP Integration Hub",
      description: "Real-time bidirectional sync with enterprise systems",
      specs: ["Native API connections", "<100ms response time", "99.99% uptime SLA"]
    },
    {
      component: "Response Generation",
      description: "Contextual response crafting with tone and relationship awareness", 
      specs: ["Multi-language support", "Brand voice customization", "Escalation logic"]
    }
  ];

  const roiMetrics = [
    {
      metric: "Up to 90%",
      description: "Exception Prevention Rate",
      detail: "Problems resolved before impacting workflow"
    },
    {
      metric: "6.5 Hours",
      description: "Daily Time Savings",
      detail: "Per AP team member average"
    },
    {
      metric: "$79K+",
      description: "Annual Cost Reduction", 
      detail: "Based on HACOV case study results"
    },
    {
      metric: "30 Seconds",
      description: "Average Response Time",
      detail: "From email receipt to intelligent reply"
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="AI Email Prevention — Automate Supplier-Email Triage | EZ Cloud"
        description="Most AP automation stops at invoice extraction. EZ Cloud's AI resolves supplier-email triage — missing POs, payment-status chasing, duplicate submissions — directly with suppliers before invoices reach your AP queue. Up to 90% of exceptions prevented."
        canonical="https://ezcloud.ai/technology/ai-email-prevention"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Platform', url: 'https://ezcloud.ai/#platform' },
          { name: 'AI Email Prevention', url: 'https://ezcloud.ai/technology/ai-email-prevention' },
        ]}
      />
      <Navigation />
      <Breadcrumbs />
      
      {/* Hero Section */}
      <section className="pt-20 pb-20 bg-hero-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-primary/10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl -translate-x-48 -translate-y-48"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl translate-x-48 translate-y-48"></div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-accent text-accent-foreground px-6 py-2 text-lg">AI Email Prevention</Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Most AP automation stops at invoice extraction.
              <span className="block text-accent">AP work doesn't.</span>
            </h1>
            <p className="text-xl lg:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
              Supplier-email triage quietly absorbs a huge share of AP capacity — payment-status
              chasing, missing PO numbers, duplicate submissions, banking-change requests, endless
              follow-ups. EZ Cloud's AI resolves these directly with suppliers, by email, before an
              invoice ever reaches your AP queue — the half of AP work invoice OCR leaves behind.
            </p>
            
            {/* Key Tech Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">10M+</div>
                <div className="text-sm text-white/80">Training Samples</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">32K</div>
                <div className="text-sm text-white/80">Token Context Window</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">&lt;100ms</div>
                <div className="text-sm text-white/80">ERP Response Time</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">24/7</div>
                <div className="text-sm text-white/80">Autonomous Operation</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-4 bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => openCalendlyDemo()}
              >
                <Activity className="w-5 h-5 mr-2" />
                See Live Demo
              </Button>
              <Button asChild size="lg" variant="outline-on-dark" className="text-lg px-8 py-4">
                <Link to="/integrations">
                  View ERP Integrations
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Technical Architecture */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
              Technical Architecture
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Built on enterprise-grade infrastructure with state-of-the-art AI models specifically trained for accounts payable communications.
            </p>
          </div>

          <div className="grid gap-8 max-w-4xl mx-auto mb-16">
            {technicalArchitecture.map((component, index) => (
              <Card key={index} className="border-none shadow-large hover:shadow-xl transition-spring bg-white">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-foreground flex items-center">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                      <div className="w-4 h-4 bg-primary rounded-full"></div>
                    </div>
                    {component.component}
                  </CardTitle>
                  <p className="text-muted-foreground">{component.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    {component.specs.map((spec, specIndex) => (
                      <div key={specIndex} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 text-accent mr-2 flex-shrink-0" />
                        {spec}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Email Processing Flow */}
          <div className="bg-section-gradient rounded-lg p-8">
            <h3 className="text-2xl font-bold text-foreground mb-8 text-center">Email Processing Flow</h3>
            <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              Watch how every email is intelligently processed through our AI pipeline in real-time
            </p>
            
            <div className="relative">
              {/* Flow Steps */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 relative">
                {/* Step 1: Email Received */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-primary/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl mb-4 mx-auto group-hover:bg-primary/20 transition-colors">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">Email Received</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4">Supplier inquiry detected</p>
                    <div className="text-xs text-center text-primary font-medium">~10ms</div>
                  </div>
                  {/* Connection Line */}
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-primary to-primary/60 transform -translate-y-1/2">
                    <div className="absolute -right-1 top-1/2 w-2 h-2 bg-primary rounded-full transform -translate-y-1/2"></div>
                  </div>
                </div>

                {/* Step 2: Security & Classification */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-accent/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                    <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-xl mb-4 mx-auto group-hover:bg-accent/20 transition-colors">
                      <Shield className="w-6 h-6 text-accent" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">Security & Intent</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4">Scan + classify purpose</p>
                    <div className="text-xs text-center text-accent font-medium">~50ms</div>
                  </div>
                  {/* Connection Line */}
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-accent to-accent/60 transform -translate-y-1/2">
                    <div className="absolute -right-1 top-1/2 w-2 h-2 bg-accent rounded-full transform -translate-y-1/2"></div>
                  </div>
                </div>

                {/* Step 3: ERP Data Lookup */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-primary/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl mb-4 mx-auto group-hover:bg-primary/20 transition-colors">
                      <Database className="w-6 h-6 text-primary" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">ERP Lookup</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4">Real-time data retrieval</p>
                    <div className="text-xs text-center text-primary font-medium">~80ms</div>
                  </div>
                  {/* Connection Line */}
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-primary to-accent transform -translate-y-1/2">
                    <div className="absolute -right-1 top-1/2 w-2 h-2 bg-primary rounded-full transform -translate-y-1/2"></div>
                  </div>
                </div>

                {/* Step 4: AI Response */}
                <div className="group relative">
                  <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-transparent group-hover:border-primary/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-xl mb-4 mx-auto group-hover:scale-110 transition-transform">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-lg font-bold text-center mb-2">AI Response</h4>
                    <p className="text-sm text-muted-foreground text-center mb-4">Generate & send reply</p>
                    <div className="text-xs text-center text-primary font-medium">~120ms</div>
                  </div>
                </div>
              </div>

              {/* Mobile Flow Connectors */}
              <div className="lg:hidden flex flex-col items-center space-y-4 absolute top-0 left-1/2 transform -translate-x-1/2 h-full">
                <div className="w-0.5 bg-gradient-to-b from-primary via-accent via-secondary to-primary h-full opacity-30"></div>
              </div>

              {/* Bottom Stats */}
              <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">260ms</div>
                  <div className="text-xs text-muted-foreground">Total Processing</div>
                </div>
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-2xl font-bold text-accent mb-1">97.3%</div>
                  <div className="text-xs text-muted-foreground">Auto-Resolve Rate</div>
                </div>
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">24/7</div>
                  <div className="text-xs text-muted-foreground">Always Active</div>
                </div>
                <div className="text-center p-4 bg-white/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">0</div>
                  <div className="text-xs text-muted-foreground">Human Intervention</div>
                </div>
              </div>

              {/* Confidence Scoring Indicator */}
              <div className="mt-8 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl p-6">
                <h4 className="text-lg font-bold text-center mb-4">Confidence-Based Routing</h4>
                <div className="flex items-center justify-between max-w-lg mx-auto">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2 mx-auto">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="text-sm font-medium">High Confidence</div>
                    <div className="text-xs text-muted-foreground">Auto-Send (95%+)</div>
                  </div>
                  <ArrowRight className="w-6 h-6 text-muted-foreground" />
                  <div className="text-center">
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-2 mx-auto">
                      <Users className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div className="text-sm font-medium">Review Queue</div>
                    <div className="text-xs text-muted-foreground">Human Review (&lt;5%)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Capabilities Deep Dive */}
      <section className="py-20 bg-section-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              AI Capabilities Deep Dive
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Understanding the advanced AI technologies that make exception prevention possible.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {aiCapabilities.map((capability, index) => (
              <Card key={index} className="border-none shadow-large hover:shadow-xl transition-spring bg-white">
                <CardHeader>
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="flex-shrink-0 p-3 bg-primary/10 rounded-lg">
                      {capability.icon}
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-foreground">{capability.title}</CardTitle>
                      <p className="text-muted-foreground mt-2">{capability.description}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-accent/10 rounded-lg p-4 border-l-4 border-accent">
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Technical Details:</strong> {capability.technical}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Real Email Scenarios */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Real Email Scenarios
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how AI handles actual supplier communications across different complexity levels.
            </p>
          </div>

          <div className="grid gap-8 max-w-6xl mx-auto">
            {emailScenarios.map((scenario, index) => (
              <Card key={index} className="border-none shadow-large bg-white">
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <CardTitle className="text-xl font-bold text-foreground">{scenario.type}</CardTitle>
                    <Badge 
                      variant={scenario.complexity === 'Critical' ? 'destructive' : 
                              scenario.complexity === 'Complex' ? 'default' : 'secondary'}
                      className="text-sm"
                    >
                      {scenario.complexity}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-foreground mb-2 flex items-center">
                        <Mail className="w-4 h-4 mr-2 text-primary" />
                        Example Email
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-primary">
                        <p className="text-sm text-muted-foreground italic">{scenario.example}</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-2 flex items-center">
                        <Brain className="w-4 h-4 mr-2 text-accent" />
                        AI Response Process
                      </h4>
                      <p className="text-sm text-muted-foreground">{scenario.response}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2 flex items-center">
                      <TrendingUp className="w-4 h-4 mr-2 text-accent" />
                      Business Impact
                    </h4>
                    <p className="text-sm text-muted-foreground">{scenario.prevention}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ROI & Performance Metrics */}
      <section className="py-20 bg-section-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Measurable Performance Impact
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Real metrics from production deployments across our customer base.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto mb-16">
            {roiMetrics.map((metric, index) => (
              <Card key={index} className="border-none shadow-large bg-white text-center p-6">
                <div className="text-4xl font-bold text-accent mb-2">{metric.metric}</div>
                <div className="text-lg font-semibold text-foreground mb-2">{metric.description}</div>
                <div className="text-sm text-muted-foreground">{metric.detail}</div>
              </Card>
            ))}
          </div>

          {/* HACOV Case Study Highlight */}
          <Card className="border-none shadow-xl bg-white max-w-4xl mx-auto">
            <CardHeader>
              <div className="text-center">
                <Badge className="mb-4 bg-accent text-accent-foreground">Featured Case Study</Badge>
                <CardTitle className="text-2xl font-bold text-foreground">Housing Authority of Covington</CardTitle>
                <p className="text-muted-foreground mt-2">Yardi Voyager Integration • Public Housing Authority</p>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-accent mb-2">71%</div>
                  <div className="text-sm text-muted-foreground">Processing Time Reduction<br/>(7 days → 2 days)</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-accent mb-2">80%</div>
                  <div className="text-sm text-muted-foreground">Error Rate Reduction<br/>(Manual → AI Prevention)</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-accent mb-2">$79K+</div>
                  <div className="text-sm text-muted-foreground">Annual Savings<br/>(FTE + Efficiency)</div>
                </div>
              </div>
              <div className="border-l-4 border-accent pl-6 bg-accent/10 rounded-r-lg p-4">
                <p className="text-muted-foreground italic">
                  "EZ Cloud has made my life in a busy accounts payable environment more efficient. The whole team was skeptical at first about how EZ Cloud and Yardi were going to work together but they have, and it has been seamless."
                </p>
                <div className="text-sm text-foreground mt-2">— Denise Steffen, AP Manager</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* RELATED RESOURCES */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                Related EZ Cloud platform & industry resources
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Where the AI Email Prevention layer fits into the broader EZ Cloud platform, the ERPs it integrates with, and the industries that rely on it most.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/technology/native-erp"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Native ERP integration
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  The integration layer underneath — real-time APIs to Oracle, NetSuite, SAP, Dynamics, Yardi.
                </p>
              </Link>

              <Link
                to="/technology/multi-platform"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Multi-platform integration hub
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Unified AI dashboard across multiple ERPs and entities — exception prevention is one layer of a larger AP control plane.
                </p>
              </Link>

              <Link
                to="/integrations"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  All ERP integrations
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Five prebuilt native integrations — Oracle, NetSuite, SAP, Dynamics 365, Yardi.
                </p>
              </Link>

              <Link
                to="/industries/healthcare"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Healthcare AP
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Where AI exception prevention compounds with HIPAA-compliant workflows, vendor credentialing, and Oracle-native posting.
                </p>
              </Link>

              <Link
                to="/industries/manufacturing"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Manufacturing AP
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  High-volume 3-way match where AI handles GRN variance and supplier email exceptions automatically.
                </p>
              </Link>

              <Link
                to="/customers"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Customer stories
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Real outcomes from EZ Cloud customers using AI exception prevention to cut AP processing time.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Forward Deployed Engineering — alternative delivery model.
          Lays groundwork for the AI Email Agents FDE positioning without
          repositioning the entire page. Soft introduction. */}
      <section className="py-20 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <Badge className="bg-accent/15 text-accent border-accent/30 mb-4">
              Delivery options
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Two ways to bring AI Email Prevention to your Oracle environment
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              The same AI Email Prevention framework deploys two ways depending on how your
              enterprise prefers to engage. Both run on Oracle Cloud Infrastructure. Both preserve
              the Oracle-supported integration patterns we publish on the{' '}
              <a
                href="https://docs.oracle.com/en/solutions/ez-cloud-on-oci/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-accent hover:text-accent"
              >
                Oracle Reference Architecture for OCI
              </a>
              .
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* SaaS option */}
            <Card className="p-7 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Workflow className="w-5 h-5 text-primary" aria-hidden />
                </div>
                <h3 className="text-xl font-bold text-gray-900">EZ Cloud SaaS</h3>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                The complete AP automation platform with AI Email Prevention built in. EZ Cloud
                runs in our managed cloud, integrates with your Oracle EBS or Fusion via supported
                integration patterns, and your team is live in 8–12 weeks.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  Fastest time to production
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  Predictable subscription pricing
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  We operate it; your team owns the outcomes
                </li>
              </ul>
            </Card>

            {/* FDE option */}
            <Card className="p-7 bg-white border border-accent/30 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-lg bg-accent/15 flex items-center justify-center">
                  <Handshake className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Forward Deployed Engineering
                </h3>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                For Oracle Enterprise customers who want the AI Email Prevention framework
                deployed inside their own OCI tenancy. We bring the reference architecture, agent
                patterns, and integration templates; our team works alongside yours to deploy,
                tune, and transfer ownership. The framework generalizes beyond AP — the same
                pattern applies to procurement, HR, customer service, and IT inboxes.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  Runs in your OCI tenancy — you own the IP
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  Founder-led engagement, knowledge transfer to your team
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  Extends to additional inbox use cases as your roadmap evolves
                </li>
              </ul>
            </Card>
          </div>

          <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
            Engagement pricing is established per situation on a Decision Call. The right
            delivery model depends on your stack, your timeline, and how your enterprise prefers
            to engage with strategic capabilities.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-hero-gradient text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Experience AI Email Prevention
            </h2>
            <p className="text-xl mb-8 text-white/90">
              See how conversational AI transforms your supplier communications and eliminates up to 90% of AP exceptions before they happen.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-4 bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={() => openCalendlyDemo()}
              >
                <Brain className="w-5 h-5 mr-2" />
                Schedule AI Demo
              </Button>
              <Button asChild size="lg" variant="outline-on-dark" className="text-lg px-8 py-4">
                <Link to="/technology/native-erp">
                  Explore ERP Architecture
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
            
            <p className="text-sm text-white/70">
              🧠 Built on enterprise AI infrastructure • ⚡ Sub-second response times • 🔒 SOC 2 compliant
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIEmailPrevention;